import React, { useEffect } from "react";
import { X, Download, ExternalLink } from "lucide-react";

/**
 * Fullscreen image lightbox modal.
 * - Closes on Esc, backdrop click, or X button.
 * - Provides Download + Open-in-new-tab actions.
 * - Locks body scroll while open.
 */
export default function ImageLightbox({ src, alt, onClose }) {
    useEffect(() => {
        if (!src) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [src, onClose]);

    if (!src) return null;

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 md:p-8"
            onClick={onClose}
            data-testid="image-lightbox"
            role="dialog"
            aria-modal="true"
        >
            <div
                className="absolute top-4 right-4 flex items-center gap-2 z-10"
                onClick={(e) => e.stopPropagation()}
            >
                <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white text-ink border border-ink hover:bg-signal hover:text-white hover:border-signal transition-colors"
                    title="Open in new tab"
                    data-testid="lightbox-open-newtab"
                >
                    <ExternalLink className="w-4 h-4" />
                </a>
                <a
                    href={src}
                    download
                    className="p-2 bg-white text-ink border border-ink hover:bg-signal hover:text-white hover:border-signal transition-colors"
                    title="Download"
                    data-testid="lightbox-download"
                >
                    <Download className="w-4 h-4" />
                </a>
                <button
                    onClick={onClose}
                    className="p-2 bg-white text-ink border border-ink hover:bg-destructive hover:text-white hover:border-destructive transition-colors"
                    title="Close (Esc)"
                    aria-label="Close lightbox"
                    data-testid="lightbox-close"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>
            <img
                src={src}
                alt={alt || "image"}
                className="max-w-full max-h-full object-contain select-none"
                onClick={(e) => e.stopPropagation()}
                data-testid="lightbox-image"
                draggable={false}
            />
        </div>
    );
}
